/**
 * Navigation.jsx — Week 4 (QA + Role cleanup + RTL Dark Mode Fix)
 * Products nav link only shown when products_page_enabled === true
 */
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useRoles } from "../../hooks/useRoles";
import {
  FiGrid, FiTarget, FiSettings, FiLogOut, FiLock,
  FiMenu, FiX, FiSun, FiMoon, FiGlobe, FiUser,
} from "react-icons/fi";
import { RiDashboardLine } from "react-icons/ri";

function useDarkMode() {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("nexusflow-theme");
    if (saved) return saved === "dark";
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });
  const toggleDarkMode = () => {
    setDarkMode(prev => {
      const next = !prev;
      localStorage.setItem("nexusflow-theme", next ? "dark" : "light");
      window.dispatchEvent(new CustomEvent("nexusflow:darkmode", { detail: next }));
      return next;
    });
  };
  return { darkMode, toggleDarkMode };
}

const Navigation = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { logout, userData, planId } = useAuth();
  const { t, toggleLang, lang, isRTL } = useLanguage();
  const { canUseCampaigns, canViewProducts } = useFeatureFlags();
  const { canAccessAdmin, getRoleLabelKey, getRoleBadgeCls } = useRoles();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const handleLogout = async () => {
    setMobileOpen(false);
    await logout();
    navigate("/login");
  };

  const navBg        = darkMode ? "bg-gray-900/95 border-gray-700/60" : "bg-white/97 border-gray-200";
  const linkActive   = "bg-blue-600 text-white shadow-md shadow-blue-600/20";
  const linkInactive = darkMode
    ? "text-gray-400 hover:text-white hover:bg-gray-700/60"
    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100";
  const linkBase  = "flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200";
  const lockStyle = "flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-gray-500 cursor-not-allowed";
  const pillBg    = darkMode ? "bg-gray-800"    : "bg-gray-100";
  const pillText  = darkMode ? "text-gray-300"  : "text-gray-700";
  const userBg    = darkMode ? "bg-gray-800/70" : "bg-gray-100";
  const userText  = darkMode ? "text-white"     : "text-gray-900";
  const subText   = darkMode ? "text-gray-400"  : "text-gray-500";
  const mobileBg  = darkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200";

  /* ── Nav links ─────────────────────────────────────────────
     Products link only rendered when products_page_enabled=true.
     Default is false — system admin must enable it per tenant.
  ─────────────────────────────────────────────────────────── */
  const navLinks = [
    {
      key: "company",
      label: t("nav_company"),
      path: "/company",
      icon: <RiDashboardLine className="text-base" />,
      locked: false,
    },
    // Only show Products if the system admin enabled it for this tenant
    ...(canViewProducts() ? [{
      key: "dashboard",
      label: t("nav_dashboard"),
      path: "/dashboard",
      icon: <FiGrid className="text-base" />,
      locked: false,
    }] : []),
    {
      key: "campaigns",
      label: t("nav_campaign_decision"),
      path: "/campaigns",
      icon: <FiTarget className="text-base" />,
      locked: !canUseCampaigns(),
    },
  ];

  const roleLabelKey = getRoleLabelKey();
  const roleBadgeCls = getRoleBadgeCls();

  return (
    <>
      <nav className={`${navBg} border-b backdrop-blur-md sticky top-0 z-50 transition-colors duration-300`}>
        <div className="flex items-center justify-between px-4 md:px-6 h-16">

          {/* Logo */}
          <button onClick={() => navigate("/company")} className="flex items-center gap-2.5 group shrink-0">
            <RiDashboardLine className={`text-2xl transition-transform group-hover:scale-110 ${darkMode ? "text-blue-400" : "text-blue-600"}`} />
            <span className={`text-xl font-bold bg-gradient-to-r bg-clip-text text-transparent ${darkMode ? "from-blue-400 to-purple-500" : "from-blue-600 to-purple-600"}`}>
              NexusFlow
            </span>
          </button>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1 mx-6">
            {navLinks.map(link => link.locked ? (
              <div key={link.key} className="relative group">
                <button className={lockStyle}>
                  {link.icon}<span>{link.label}</span>
                  <FiLock className="text-xs ml-1 opacity-60" />
                </button>
                <div className="absolute left-0 top-full mt-2 hidden group-hover:block z-50 pointer-events-none">
                  <div className="bg-gray-900 border border-gray-700 rounded-xl p-3 shadow-2xl whitespace-nowrap text-xs">
                    <p className="text-gray-300 mb-0.5">🔒 {t("not_available")}</p>
                    <p className="text-gray-500">{t("upgrade_to_unlock")}</p>
                  </div>
                </div>
              </div>
            ) : (
              <button key={link.key} onClick={() => navigate(link.path)}
                className={`${linkBase} ${isActive(link.path) ? linkActive : linkInactive}`}>
                {link.icon}<span>{link.label}</span>
              </button>
            ))}

            {canAccessAdmin() && (
              <button onClick={() => navigate("/admin")}
                className={`${linkBase} ${isActive("/admin") ? linkActive : linkInactive}`}>
                <FiSettings className="text-base" />
                <span>{t("nav_admin")}</span>
              </button>
            )}
          </div>

          {/* Desktop right controls */}
          <div className="hidden md:flex items-center gap-2 shrink-0">
            <button onClick={toggleLang}
              title={lang === "en" ? "Switch to Arabic / العربية" : "Switch to English"}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-semibold transition-all ${pillBg} ${pillText} hover:ring-2 hover:ring-blue-500/40`}>
              <FiGlobe className="text-sm" />
              <span>{lang === "en" ? "عربي" : "EN"}</span>
            </button>

            <motion.button onClick={toggleDarkMode} whileTap={{ scale: 0.9 }}
              className={`relative w-11 h-6 rounded-full transition-colors duration-300 ${darkMode ? "bg-blue-600" : "bg-gray-300"}`}
              aria-label="Toggle theme">
              <motion.div
                className={`absolute top-0.5 w-5 h-5 rounded-full flex items-center justify-center shadow-md ${darkMode ? "bg-gray-900" : "bg-white"}`}
                // initial={false} — render at the target position on mount,
                // no animation. Without this, every route change remounts
                // Navigation and the slider visibly jumps from left:0 to its
                // target. Toggling still animates smoothly because animate
                // changes are still respected after mount.
                initial={false}
                animate={{ left: darkMode ? "22px" : "2px" }}
                transition={{ type: "spring", stiffness: 400, damping: 25 }}>
                {darkMode ? <FiMoon className="text-blue-400 text-xs" /> : <FiSun className="text-yellow-500 text-xs" />}
              </motion.div>
            </motion.button>

            {/* Plan badge — driven by the canonical planId from
                AuthContext (resolves tenant.planId / tenant.plan with
                a legacy subscriptionTier fallback). The previous read
                of userData.subscriptionTier was a phantom field and
                always evaluated falsy, so this badge never rendered. */}
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
              planId === "enterprise" ? "bg-purple-500/10 text-purple-400 border-purple-500/30" :
              planId === "pro"        ? "bg-blue-500/10 text-blue-400 border-blue-500/30" :
              darkMode ? "bg-gray-800 text-gray-400 border-gray-700" : "bg-gray-100 text-gray-500 border-gray-300"
            }`}>
              {planId === "enterprise" ? `⭐ ${t("plan_enterprise")}` :
               planId === "pro"        ? `💎 ${t("plan_pro")}` :
                                         t("plan_free")}
            </span>

            <div className={`flex items-center gap-2.5 ${userBg} rounded-xl px-3 py-1.5`}>
              {/* Clicking the name+role pill opens the profile page.
                  Logout stays as a separate button so an accidental
                  click on it doesn't sign the user out. */}
              <button
                type="button"
                onClick={() => navigate("/profile")}
                title={t("profile_link")}
                className="flex items-center gap-2.5 cursor-pointer"
              >
                <FiUser className={`text-sm ${subText} shrink-0`} />
                <div className="text-left">
                  <p className={`text-sm font-semibold ${userText} leading-tight hover:underline`}>
                    {userData?.displayName || "User"}
                  </p>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full border font-medium leading-none ${roleBadgeCls}`}>
                    {t(roleLabelKey)}
                  </span>
                </div>
              </button>
              <button onClick={handleLogout} title={t("nav_logout")}
                className={`p-1.5 rounded-lg transition-colors ${darkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"}`}>
                <FiLogOut className={`text-sm ${subText}`} />
              </button>
            </div>
          </div>

          {/* Mobile burger */}
          <button onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden p-2 rounded-xl transition-colors ${darkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}>
            {mobileOpen ? <FiX className={`text-xl ${pillText}`} /> : <FiMenu className={`text-xl ${pillText}`} />}
          </button>
        </div>

        {/* Mobile dropdown */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22 }} className="overflow-hidden md:hidden">
              <div className={`${mobileBg} border-t p-4 space-y-2`}>
                {navLinks.map(link => (
                  <button key={link.key}
                    onClick={() => { if (!link.locked) { navigate(link.path); setMobileOpen(false); } }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                      link.locked ? "text-gray-500 cursor-not-allowed"
                      : isActive(link.path) ? "bg-blue-600 text-white"
                      : linkInactive
                    }`}>
                    {link.icon}<span>{link.label}</span>
                    {link.locked && <FiLock className="ml-auto text-xs opacity-60" />}
                  </button>
                ))}

                {canAccessAdmin() && (
                  <button onClick={() => { navigate("/admin"); setMobileOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium ${isActive("/admin") ? "bg-blue-600 text-white" : linkInactive}`}>
                    <FiSettings /><span>{t("nav_admin")}</span>
                  </button>
                )}

                <div className={`flex items-center justify-between pt-3 border-t ${darkMode ? "border-gray-700" : "border-gray-200"}`}>
                  <button onClick={toggleLang} className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold ${pillBg} ${pillText}`}>
                    <FiGlobe /><span>{lang === "en" ? "عربي" : "EN"}</span>
                  </button>
                  <motion.button onClick={toggleDarkMode}
                    className={`relative w-11 h-6 rounded-full ${darkMode ? "bg-blue-600" : "bg-gray-300"}`}>
                    <motion.div
                      className={`absolute top-0.5 w-5 h-5 rounded-full flex items-center justify-center shadow-md ${darkMode ? "bg-gray-900" : "bg-white"}`}
                      initial={false}
                      animate={{ left: darkMode ? "22px" : "2px" }}
                      transition={{ type: "spring", stiffness: 400, damping: 25 }}>
                      {darkMode ? <FiMoon className="text-blue-400 text-xs" /> : <FiSun className="text-yellow-500 text-xs" />}
                    </motion.div>
                  </motion.button>
                </div>

                <div className={`flex items-center justify-between ${userBg} rounded-xl px-4 py-3`}>
                  <button
                    type="button"
                    onClick={() => { setMobileOpen(false); navigate("/profile"); }}
                    className="text-left cursor-pointer"
                  >
                    <p className={`text-sm font-semibold ${userText} hover:underline`}>{userData?.displayName || "User"}</p>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full border font-medium ${roleBadgeCls}`}>
                      {t(roleLabelKey)}
                    </span>
                  </button>
                  <button onClick={handleLogout}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${darkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"} ${subText}`}>
                    <FiLogOut /><span>{t("nav_logout")}</span>
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

export default Navigation;